import React from 'react';
import { withRouter } from 'react-router';
import moment from 'moment';
import { Text, CheckBox } from 'components';
import { Table, Icon } from 'semantic-ui-react';
import { Mail, Ship, Edit3, CheckCircle2, Clock, AlertCircle } from 'lucide-react';

import MemberAvatar from './member-avatar/MemberAvatar';
import CrewStatusBadge from './crew-status-badge/CrewStatusBadge';
import MemberActionMenu from './member-action-menu/MemberActionMenu';
import { resolveCrewDates } from '../utils/resolve-crew-dates';
import { computeCrewStatus, CREW_STATUS } from '../utils/compute-crew-status';

import './employeesTable.scss';

// Legacy inline status component (used when useNewUI is false)
const statuses = {
  'ACTIVE': { color: 'green' },
  'UN-ASSIGNED': { color: 'grey' },
  'INVITED': { color: 'blue' },
  'RE-INVITE': { color: 'yellow' },
  'BOUNCED': { color: 'red' },
};

const SignUpStatus = ({ status }) => {
  const stat = statuses[status];
  return (
    <div className={`table-status table-status--${stat?.color}`}>
      <div className="table-status__dot" />
      <Text size="small" color={stat?.color} bold>{status}</Text>
    </div>
  );
};

const EmployeesTable = ({
  selectedRows, filteredData, onRowSelect, selectAllRows,
  onEditClick, formattedDepts, hasEditRights,
  useNewUI = false,
  onViewProfile, onAssignVessel, onAssignClinic, onContactMember, onDeleteMember,
}) => {

  // New UI table (Figma layout: MEMBER | RANK & DEPT | ASSIGNED VESSEL | MEDICAL CLINIC | STATUS | SCHEDULE | ACTIONS)
  if (useNewUI) {
    return (
      <div className="tw-overflow-x-auto">
        <table className="crew-table tw-w-full tw-text-left">
          <thead>
            <tr className="tw-bg-gray-50 tw-text-xs tw-uppercase tw-tracking-wider tw-text-gray-500 tw-font-semibold">
              {hasEditRights && (
                <th className="tw-p-4 tw-w-10">
                  <div onClick={(e) => selectAllRows(e)} className="tw-cursor-pointer">
                    <CheckBox
                      className="emp-table__checkbox"
                      checked={Object.keys(selectedRows)?.length === filteredData?.length && filteredData?.length > 0}
                    />
                  </div>
                </th>
              )}
              <th className="tw-p-4">Member</th>
              <th className="tw-p-4">Rank &amp; Dept</th>
              <th className="tw-p-4">Assigned Vessel</th>
              <th className="tw-p-4">Medical Clinic</th>
              <th className="tw-p-4">Status</th>
              <th className="tw-p-4">Schedule</th>
              {hasEditRights && (
                <th className="tw-p-4 tw-text-right">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {filteredData.map((emp) => {
              const { signOnDate: resolvedSignOn, signOffDate: resolvedSignOff } = resolveCrewDates(emp);
              const crewStatus = computeCrewStatus(emp);
              const isOnLeave = [CREW_STATUS.ON_LEAVE, CREW_STATUS.SICK_LEAVE].includes(crewStatus);
              const scheduleDate = isOnLeave && resolvedSignOff ? resolvedSignOff : resolvedSignOn;
              const scheduleLabel = isOnLeave ? 'Available' : (scheduleDate ? 'Signed On' : 'Not Scheduled');
              return (<tr
                key={emp?._id}
                className={[
                  'tw-group hover:tw-bg-gray-50 tw-transition-colors',
                  selectedRows[emp._id] ? 'tw-bg-indigo-50' : '',
                ].join(' ')}
              >
                {hasEditRights && (
                  <td className="tw-p-4" onClick={(e) => onRowSelect(e, emp._id, emp)}>
                    <CheckBox
                      className="emp-table__checkbox"
                      checked={!!selectedRows[emp._id]}
                    />
                  </td>
                )}

                {/* Member: avatar + name + email with icon */}
                <td className="tw-p-4">
                  <div className="tw-flex tw-items-center tw-gap-3">
                    <MemberAvatar firstName={emp?.firstName} lastName={emp?.lastName} />
                    <div className="tw-flex tw-flex-col">
                      <span className="tw-text-sm tw-font-semibold tw-text-gray-900">
                        {emp?.firstName} {emp?.lastName}
                      </span>
                      {emp?.email && (
                        <div className="tw-flex tw-items-center tw-gap-1 tw-mt-0.5">
                          <Mail className="tw-w-3 tw-h-3 tw-text-gray-400 tw-flex-shrink-0" />
                          <span className="tw-text-xs tw-text-gray-500">
                            {emp.email}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </td>

                {/* Rank & Dept: combined column */}
                <td className="tw-p-4">
                  <div className="tw-flex tw-flex-col">
                    <span className="tw-text-sm tw-font-medium tw-text-gray-900">
                      {emp?.designation || '—'}
                    </span>
                    <span className="tw-text-xs tw-text-gray-500">
                      {emp?.departments?.length ? formattedDepts[emp?.departments[0]] : '—'}
                    </span>
                  </div>
                </td>

                {/* Assigned Vessel */}
                <td className="tw-p-4">
                  {emp?.vesselName ? (
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <div className="tw-p-1.5 tw-bg-blue-50 tw-text-blue-600 tw-rounded-md">
                        <Ship className="tw-w-4 tw-h-4" />
                      </div>
                      <span className="tw-text-sm tw-font-medium tw-text-gray-700">{emp.vesselName}</span>
                    </div>
                  ) : (
                    <span className="tw-text-sm tw-text-gray-400 tw-italic tw-pl-1">Unassigned</span>
                  )}
                </td>

                {/* Medical Clinic */}
                <td className="tw-p-4">
                  {emp?.clinicName ? (
                    <div className="tw-flex tw-flex-col">
                      <span className="tw-text-sm tw-text-gray-900 tw-font-medium tw-truncate tw-max-w-[140px]" title={emp.clinicName}>
                        {emp.clinicName}
                      </span>
                      {emp?.medicalStatus && (
                        <div className={`tw-flex tw-items-center tw-gap-1 tw-text-xs tw-mt-0.5 ${
                          emp.medicalStatus === 'Unfit' ? 'tw-text-red-700'
                            : emp.medicalStatus === 'Expires Soon' ? 'tw-text-amber-700'
                            : 'tw-text-emerald-700'
                        }`}>
                          {emp.medicalStatus === 'Unfit'
                            ? <AlertCircle className="tw-w-3 tw-h-3 tw-text-red-500" />
                            : emp.medicalStatus === 'Expires Soon'
                            ? <Clock className="tw-w-3 tw-h-3 tw-text-amber-500" />
                            : <CheckCircle2 className="tw-w-3 tw-h-3 tw-text-emerald-500" />
                          }
                          <span>{emp.medicalStatus}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className="tw-text-gray-400 tw-text-sm tw-italic">Not assigned</span>
                  )}
                </td>

                {/* Status */}
                <td className="tw-p-4">
                  <CrewStatusBadge status={emp?.signupStatus} signOnDate={resolvedSignOn} signOffDate={resolvedSignOff} />
                </td>

                {/* Schedule */}
                <td className="tw-p-4">
                  {scheduleDate ? (
                    <div className="tw-text-sm">
                      <span className="tw-text-gray-500 tw-text-xs tw-block tw-uppercase tw-tracking-wide">
                        {scheduleLabel}
                      </span>
                      <span className="tw-text-sm tw-font-medium tw-text-gray-900">
                        {moment(scheduleDate, ['DD-MM-YYYY', 'YYYY/MM/DD']).format('MMM D, YYYY')}
                      </span>
                    </div>
                  ) : (
                    <span className="tw-text-gray-300 tw-text-lg">—</span>
                  )}
                </td>

                {/* Actions: edit + "..." menu */}
                {hasEditRights && (
                  <td className="tw-p-4">
                    <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
                      <button
                        onClick={() => onEditClick(emp)}
                        className="tw-p-1.5 tw-text-gray-500 hover:tw-text-indigo-600 hover:tw-bg-indigo-50 tw-rounded-lg tw-transition-colors tw-border-0 tw-bg-transparent tw-cursor-pointer"
                        title="Edit Profile"
                      >
                        <Edit3 className="tw-h-4 tw-w-4" />
                      </button>
                      <MemberActionMenu
                        emp={emp}
                        onViewProfile={onViewProfile}
                        onEdit={onEditClick}
                        onAssignVessel={onAssignVessel}
                        onAssignClinic={onAssignClinic}
                        onContact={onContactMember}
                        onDelete={onDeleteMember}
                        hasEditRights={hasEditRights}
                      />
                    </div>
                  </td>
                )}
              </tr>);
            })}
          </tbody>
        </table>
      </div>
    );
  }

  // Legacy UI table (unchanged)
  const renderTableData = () => {
    return filteredData.map((emp) => {
      return (
        <Table.Row key={emp?._id}>
          {hasEditRights && (
            <Table.Cell onClick={(e) => onRowSelect(e, emp._id, emp)}>
              <div>
                <CheckBox
                  className="emp-table__checkbox"
                  checked={!!selectedRows[emp._id]}
                />
              </div>
            </Table.Cell>
          )}

          <Table.Cell>
            <div style={{ display: 'flex' }}>
              <Text size="small" className="mr-1" bold>{emp?.firstName}</Text>
              <Text size="small" bold>{emp?.lastName}</Text>
            </div>
            <Text size="tiny">{emp?.email}</Text>
          </Table.Cell>

          <Table.Cell>
            <Text size="tiny" bold>{emp?.departments?.length ? formattedDepts[emp?.departments[0]] : ''}</Text>
          </Table.Cell>

          <Table.Cell>
            <Text size="small" bold>{emp?.designation}</Text>
          </Table.Cell>

          <Table.Cell>
            <SignUpStatus status={emp?.signupStatus} />
          </Table.Cell>

          <Table.Cell>
            <Text size="tiny" bold>{emp?.crewStatus}</Text>
          </Table.Cell>

          <Table.Cell>
            <Text size="tiny" bold>{emp?.signOffDate || emp?.metadata?.signOffDate}</Text>
          </Table.Cell>

          <Table.Cell>
            <Text size="tiny" bold>{emp?.signOnDate || emp?.metadata?.signOnDate}</Text>
          </Table.Cell>

          <Table.Cell>
            <Text size="small" bold>{moment(emp?.updatedAt).format('DD MMM YYYY, HH:mm a')}</Text>
          </Table.Cell>

          {hasEditRights && (
            <Table.Cell>
              <Icon name="edit outline" className="emp-table__icon" onClick={() => onEditClick(emp)} />
            </Table.Cell>
          )}
        </Table.Row>
      );
    });
  };

  return (
    <Table className="emp-table" striped>
      <Table.Header>
        <Table.Row>
          {hasEditRights && (
            <Table.HeaderCell className="emp-table__checkbox-cell">
              <div onClick={(e) => selectAllRows(e)}>
                <CheckBox
                  className="emp-table__checkbox"
                  checked={Object.keys(selectedRows)?.length === filteredData?.length}
                />
              </div>
            </Table.HeaderCell>
          )}
          <Table.HeaderCell>Member</Table.HeaderCell>
          <Table.HeaderCell>Department</Table.HeaderCell>
          <Table.HeaderCell>Designation</Table.HeaderCell>
          <Table.HeaderCell>Sign-up Status</Table.HeaderCell>
          <Table.HeaderCell>Crew Status</Table.HeaderCell>
          <Table.HeaderCell>Sign off Date</Table.HeaderCell>
          <Table.HeaderCell>Sign on Date</Table.HeaderCell>
          <Table.HeaderCell>Latest Action Update</Table.HeaderCell>
          {hasEditRights && (
            <Table.HeaderCell className="emp-table__checkbox-cell" />
          )}
        </Table.Row>
      </Table.Header>
      <Table.Body>{renderTableData()}</Table.Body>
    </Table>
  );
};

export default withRouter(EmployeesTable);
